/**
 * The Platform class represents a generic wrapper around
 * asset-specific platforms. For example, DX1 is a power sports
 * specific platform. The generic platform class contains functionality used
 * by _all_ platforms, regardless of asset type..
 */
import AssetWrapper, { AssetWrapperConstructor } from './../asset/wrappers';
import Modal from './../modal';
import { appendTagToElement, StyleTag } from '../util/create-style-tag';
import Asset from '../asset/asset';
import { AssetWrappersLoaded, OlError, OlWarning, PageTypeDetermined, PageUpdated, StartPrequal } from '../events';
import { PageNotReady } from '../errors';
import formatUrl from '../util/format-url';
import configuration from '../configuration';
import { AssetHTMLElement } from '../typings';
import logException from '../util/log-exception';
// @ts-ignore import ECMAScript modules
import { vwoTest } from '../vwo-testing';

import { OlGtmController } from '../util/ol-gtm-controller';
import isIterable from '../util/is-iterable';
export enum PageTypes {
  SRP = 'srp',
  VDP = 'vdp',
  VDP_LEGACY = 'vdp_legacy', // Alternative type of VDP
  SRP_LEGACY = 'srp_legacy', // Alternative type of SRP
  SUMMARY = 'summary', // CFMOTO specific
  HOMEPAGE = 'homepage',
  GENERAL = 'general'
}

export type PlatformAssetWrapperMap = {
  // Key is the string value of WidgetPlatform
  // Value is a static string
  [key in PageTypes]?: AssetWrapperConstructor | null;
};

export default abstract class Platform {
  assetWrappers?: AssetWrapper<Asset>[];
  readonly modal: Modal = new Modal();
  readonly configuration = configuration;
  _pageType?: PageTypes | null;
  abstract pageTypeMap: PlatformAssetWrapperMap;
  mutationObserver?: MutationObserver;
  readonly eventBus: EventTarget = new EventTarget();
  protected _lastCallbackIndex = 0;
  protected _callbacks: {
    [identifier: number]: {
      func: (...args: any) => void;
      args: any[];
    };
  } = {};
  protected _renderLock = false;
  protected olGtmController: OlGtmController = new OlGtmController(configuration.googleTagId, this.eventBus);
  protected nextCycleCallbacksEnabled = false;
  protected initComplete = false;

  constructor() {
    // Store platform on the window object for debugging
    window.olCtaPlatform = this;
    try {
      this.testVWO();
    } catch (error) {
      this.eventBus.dispatchEvent(new OlError({ detail: { error, msg: 'Failed to initialize VWO script' } }));
    }

    // Set up error event handlers. Log the message, and specifically log the error
    // if it's available so the trace shows up in the browser console
    this.eventBus.addEventListener('ol-warning', (event) => {
      console.error(
        `a non-fatal error has occurred:`,
        event.detail.msg ? `${event.detail.msg}:\n` : '\n',
        event.detail.error ? `${event.detail.error}:\n` : '\n',
        event.detail
      );

      void logException(event.detail.error);
    });

    this.eventBus.addEventListener('ol-error', (event) => {
      console.error(
        `a fatal error has occurred:`,
        event.detail.msg ? `${event.detail.msg}:\n` : '\n',
        event.detail.error ? `${event.detail.error}:\n` : '\n',
        event.detail
      );

      void logException(event.detail.error);
    });

    // Set up Render Handler once page type is determined
    this.eventBus.addEventListener('page-type-determined', (e: Event) => {
      this.watchElements.call(this);
      void this._render.call(this, e);
    });

    this.eventBus.addEventListener('start-prequal', this.showPrequalApplication.bind(this));

    // Set up event handler for GTM
    this.eventBus.addEventListener('start-prequal', () => {
      void this.olGtmController.pushPrequalImpression();
    });
    this.eventBus.addEventListener('asset-wrappers-loaded', (e: AssetWrappersLoaded) => {
      void this.olGtmController.pushViewItemIntermediary(e);
    });

    this.eventBus.addEventListener('page-updated', (e: Event) => {
      void this._render.call(this, e);
    });
    // First task is determining page type
    this.onNextCycle(this._tryDeterminePageType.bind(this));
  }

  init(): Platform {
    this.initComplete = true;
    // Kick off nextCycle loop
    this.enableNextCycle();
    return this;
  }

  showPrequalApplication(e: StartPrequal) {
    // keep a local reference to asset for the listener
    const asset = e.detail.asset;
    const ctaType = e.detail.cta_type;
    asset.origin_url = window.location.href;

    const url = formatUrl(`${configuration.urls.uxBaseUrl}/apply/`);
    const iframe = Modal.renderIframeElement(url);

    const spinner = Modal.renderSpinnerElement();
    const iframeContainer = document.createElement('div');
    iframeContainer.style.height = iframeContainer.style.width = '100%';
    iframeContainer.appendChild(iframe);
    iframe.style.display = 'none';
    iframeContainer.appendChild(spinner);

    // Once the iframe has loaded, post the Asset Data to the UX server
    iframe.addEventListener('load', () => {
      if (!iframe.contentWindow) {
        return console.error('failed to post message to iframe Content Window: Content window is undefined');
      }
      spinner.style.display = 'none';
      iframe.style.display = '';
      const message = { ...asset.toUxServer(), cta_type: ctaType };
      iframe.contentWindow.postMessage(message, configuration.urls.uxBaseUrl);
      const contentWindow = iframe.contentWindow;
      setTimeout(() => {
        contentWindow.postMessage(message, configuration.urls.uxBaseUrl);
      }, 1000);
    });

    this.modal.showModal(iframeContainer);
  }

  get pageType(): PageTypes {
    if (!this._pageType) this._pageType = this.determinePageType();
    if (!this._pageType) throw new Error('Page type could not be determined by the platform.');
    return this._pageType;
  }

  /**
   * Internal function to continuously attempt to determine pageType until a pageType is found
   * @protected
   */
  protected _tryDeterminePageType(): void {
    // Try to get page type
    try {
      this._pageType = this.determinePageType();
      this.eventBus.dispatchEvent(new PageTypeDetermined({ detail: { pageType: this.pageType } }));
    } catch (error) {
      if (error instanceof PageNotReady) {
        console.debug('page type not yet determined');
        if (document.readyState == 'complete') {
          // If the page has loaded completely, send warning and run MutationObserver on document.body
          console.warn('OL Page type not supported: Page type could not be determined before document was ready');
          this.addBodyMutationObserver();
          return;
        }
        // If the page isn't ready yet, try again next cycle
        this.onNextCycle(this._tryDeterminePageType.bind(this));
        return;
      }

      // Unhandled errors shouldn't occur, so when they do - consider them fatal
      this.eventBus.dispatchEvent(
        new OlError({
          detail: { error, msg: 'unhandled error determining page type' }
        })
      );
    }
  }

  // Very slow loading webpages can cause Dum-E to throw “OL Page Type Not Supported”
  // on valid webpages due to the required components not being ready.
  // This sets a MutationObserver on document.body to solve that
  addBodyMutationObserver() {
    const domObserver = new MutationObserver(() => {
      try {
        this._pageType = this.determinePageType();
        this.eventBus.dispatchEvent(new PageTypeDetermined({ detail: { pageType: this.pageType } }));
        domObserver.disconnect();
        console.warn('OL Page type was determined using MutationObserver');
        return;
      } catch (error) {
        /* ¯\_(ツ)_/¯ */
        return;
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Enqueue a callback for the next "cycle". The next cycle will depend on browser;
   *   For most browsers, it will be the next requestAnimationFrame
   *   For other browsers, default to a 1-second timeout
   * @param cb Callback function to execute
   * @param args Arguments to call CB with
   * @return Callback index/identifier
   */
  onNextCycle(cb: (...args: any) => void, args?: any[]): number {
    const index = (this._lastCallbackIndex += 1); // Increment index and store value in local variable

    this._callbacks[index] = {
      func: cb,
      args: args || []
    };
    this.enableNextCycle();

    // Return the index/identifier of the callback for cancellation later
    return index;
  }

  private enableNextCycle() {
    if (this.nextCycleCallbacksEnabled || !this.initComplete) {
      return; // already setup or init not called yet, nothing to do yet
    }
    this.nextCycleCallbacksEnabled = true;
    if (window.requestAnimationFrame) {
      // Most browsers support requestAnimationFrame
      window.requestAnimationFrame(() => {
        this.nextCycleCallbacksEnabled = false;
        this._handleNextCycle();
      });
    } else {
      // If requestAnimationFrame is unsupported, wait 1 second
      setTimeout(() => {
        this.nextCycleCallbacksEnabled = false;
        this._handleNextCycle();
      }, 1000);
    }
  }

  private _handleNextCycle() {
    const callbacks = this._callbacks;
    this._callbacks = [];

    // Iterate through callbacks
    for (const i in callbacks) {
      const func = callbacks[i].func;
      const args = callbacks[i].args;
      try {
        // safety checks
        if (typeof func !== 'function') {
          this.eventBus.dispatchEvent(
            new OlWarning({ detail: { message: '_handleNextCycle received non-callable function', func, args } })
          );
          continue;
        }

        if (!isIterable(args)) {
          this.eventBus.dispatchEvent(
            new OlWarning({
              detail: { message: '_handleNextCycle received non-iterable arguments object', func, args }
            })
          );
          continue;
        }
        /* eslint @typescript-eslint/no-unsafe-argument: "off" */ // Ignoring spread of any[]
        func(...args);
      } catch (error) {
        this.eventBus.dispatchEvent(
          new OlWarning({ detail: { error, description: 'failed to execute NextCycle Callback' } })
        );
      }
    }
  }

  /**
   * This method returns the constructor function of the AssetWrapper subclass
   * that should be used to create the objects that represent an asset.
   * @returns
   */
  getAssetWrapperClass(): AssetWrapperConstructor {
    const assetClassWrapper = this.pageTypeMap[this.pageType];
    if (!assetClassWrapper) throw new Error('PageType has no corresponding AssetWrapper class');
    return assetClassWrapper;
  }

  /**
   * Create Asset Wrappers using the abstract implementation of getAssetContainers. The function getAssetContainers
   * may throw a PageNotReady error, in which case we need to queue up this functionality for the nextCycle.
   * This function is async to support waiting until the page is ready before returning
   */
  async createAssetWrappers(): Promise<AssetWrapper[]> {
    // Define a new callback function that will be passed into the Promise constructor. This needs to
    // have a named variable, so we can call onNextCycle with it
    const cb = (resolve: (value: AssetWrapper[]) => void, reject: (reason: any) => void) => {
      console.log('getting asset wrappers');
      let assetsContainers;
      try {
        // Try to get asset containers from the abstract method
        assetsContainers = this.getAssetContainers();
      } catch (error) {
        if (error instanceof PageNotReady) {
          // If a PageNotReady error is thrown, wait until next cycle and try again
          this.onNextCycle(cb.bind(this), [resolve, reject]);
          return;
        }
        // Any other errors are fatal, reject the promise
        return reject(error);
      }
      // Finish setting up the Asset Wrappers
      const AssetWrapperClass = this.getAssetWrapperClass();
      const assetWrappers = new Array<AssetWrapper>();
      for (const [index, assetContainer] of assetsContainers.entries()) {
        const assetContainerElement: AssetHTMLElement = assetContainer as AssetHTMLElement;
        if (assetContainerElement.olAssetWrapper) {
          assetWrappers.push(assetContainerElement.olAssetWrapper);
        } else {
          assetWrappers.push(new AssetWrapperClass(assetContainer, this));
        }
        assetContainerElement.olParserIndex = index;
      }
      this.eventBus.dispatchEvent(
        new AssetWrappersLoaded({
          detail: {
            assetWrappers: assetWrappers,
            pageType: this.pageType
          }
        })
      );
      // Return Asset Wrappers to the Promise holder
      return resolve(assetWrappers);
    };

    // Return the promise that will be resolved with AssetWrappers, or rejected with an Error.
    return new Promise(cb.bind(this));
  }

  /**
   * This method is responsible for disconnecting the MutationObserver from the
   * parent assets container, if one exists, calling the real render function, and
   * attaching the MutationObserver again to the parent assets container.
   *
   * This is required to prevent any infinite loop because the rendering calls
   * trigger mutation events and call more rendering operations.
   *
   * When there is need to use the render method as callback, this one should be used.
   *
   * This private _render should not be overwritten by any subclass.
   */
  protected async _render(e?: Event, forceCtaRender?: boolean): Promise<void> {
    this.mutationObserver?.disconnect();
    await this.render(e, forceCtaRender);
    this.watchElements();
  }

  /**
   * Render all the CTA Classes. This typically just involves generating wrappers and calling AssetWrapper.render()
   * This can be over-ridden if needed to handle special scenarios
   */
  async render(e?: Event, forceCtaRender?: boolean): Promise<void> {
    if (!this._renderLock) {
      this._renderLock = true;
      try {
        this.assetWrappers = await this.createAssetWrappers();
        if (!(this.assetWrappers && this.assetWrappers.length)) throw new Error('AssetWrappers were not created.');
        for (const wrapper of this.assetWrappers) {
          wrapper.render(e, forceCtaRender);
        }
      } catch (error) {
        this.eventBus.dispatchEvent(
          new OlError({
            detail: {
              error,
              msg: 'Failed to render Asset Wrappers'
            }
          })
        );
      } finally {
        this._renderLock = false;
      }
    } else {
      console.warn('second call to _render occurred during handling of the first');
    }
  }

  /**
   * Adds a mutation observer to the page container if the function
   * shouldWatchElements() returns true
   * @returns
   */
  watchElements(): void {
    const AssetWrapperClass = this.getAssetWrapperClass();
    if (!AssetWrapperClass.shouldWatchElements()) return;
    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver((mutationsRecords: MutationRecord[]) => {
        const filteredMutations = mutationsRecords.filter((mutationRecord) => {
          return !Array.from((<HTMLElement>mutationRecord.target).classList).find((className) =>
            className.startsWith('ol-')
          );
        });
        if (filteredMutations.length > 0) {
          this.eventBus.dispatchEvent(new PageUpdated({ detail: { mutations: mutationsRecords } }));
        }
      });
    }
    this.mutationObserver.observe(
      AssetWrapperClass.getPageContainer(),
      AssetWrapperClass.getPageUpdateMutationObserverOptions()
    );
  }

  testVWO(): void {
    if (configuration.enable_vwo_testing) {
      /* eslint-disable */
      vwoTest();
    }
    /* eslint-enable */
  }

  /**
   * Add a CSS style on the document level, appending it as a child of the page HEAD element
   * @param stylesElements
   */
  addStyleToDocument(stylesElements: StyleTag[]): void {
    stylesElements.forEach((styleElement) => {
      appendTagToElement(window.document.head, styleElement);
    });
  }

  //
  // The following abstract methods must be implemented by a class extending this one
  //

  /**
   * Determine the type of page currently loaded. This will be one of the page types from the PageType Enum.
   * This function is automatically called every 'cycle' until a non-null value is returned.
   */
  abstract determinePageType(): PageTypes;

  /**
   * Get HTML Elements that contain an Asset we may render a CTA within. This should include _all_
   * assets within a page; we will determine later if the Asset should have a CTA rendered.
   */
  abstract getAssetContainers(): HTMLElement[];
}
